import React, { useEffect, useState } from 'react'
import { validatePhone, validateUrl } from 'helper/utility'
import { Button, Col, Form, Input, Row } from 'antd'
import { CaretLeft } from '@phosphor-icons/react'
import PhoneInput from 'react-phone-input-2'
import { Phone } from 'types/globalTypes'
import EventManager from 'pages/Events/Views/CreateEvent/Screens/GeneralDetails/InputComps/EventManager'
import { OrgOrganizerParams } from 'types/organization'
import { useGetOrgOrganizers } from 'api/Organizations'
import { useLeagueStep2 } from 'api/league/LeagueCreation/leagueCreation'
import { leagueEventStore } from 'state/league/leagueEvent'
import { LeagueStep2Payload } from 'types/league/league'
import ButtonSpinner from 'components/global/ButtonSpinner'

interface Step2Props {
  handleNext: () => void
  handleBack: () => void
}

const Step2: React.FC<Step2Props> = ({ handleNext, handleBack }) => {
  const { leagueData, setLeagueData } = leagueEventStore()
  const [form] = Form.useForm()
  const [phone, setPhone] = useState<Phone>({
    dialCode: leagueData?.phone?.dialCode || '',
    isoCode: leagueData?.phone?.isoCode || '',
    number: leagueData?.phone?.number || '',
  })
  const [selectedOrganizer, setSelectedOrganizer] = useState<string[]>([])
  const [params, setParams] = useState<OrgOrganizerParams>({
    status: 'approved',
  })
  const [defaultOrganizer, setDefaultOrganizer] = useState(null)

  const {
    data: orgOrganizers,
    isLoading: orgOrganizersLoading,
    refetch: orgOrganizersRefetch,
  } = useGetOrgOrganizers(params, leagueData.organizationId)

  const { mutate: leagueStep2Mutation, isLoading: leagueStep2Loading } =
    useLeagueStep2()

  const formSchema = {
    website: leagueData?.website || '',
    email: leagueData?.email || '',
    phone: leagueData ? leagueData.phone?.number : undefined,
  }

  const onFinish = (
    values: Omit<LeagueStep2Payload, 'phone' | 'leagueStaffIds'>,
  ) => {
    const payload = { ...values, leagueStaffIds: selectedOrganizer, phone }

    leagueStep2Mutation(
      { leagueId: leagueData._id, payload },
      {
        onSuccess: data => {
          setLeagueData({ ...leagueData, ...data })
          handleNext()
        },
      },
    )
  }

  useEffect(() => {
    if (leagueData) {
      orgOrganizersRefetch()
    }
  }, [])

  useEffect(() => {
    setSelectedOrganizer(leagueData.leagueStaffIds || [])
  }, [leagueData])

  useEffect(() => {
    if (!orgOrganizersLoading) {
      if (orgOrganizers) {
        const owner = orgOrganizers?.find(item => item.accessLevel === 'owner')
        setDefaultOrganizer(owner)
        setSelectedOrganizer([owner.organizerId])
      }
    }
  }, [orgOrganizersLoading])

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        initialValues={formSchema}
        onFinish={onFinish}
      >
        <Row>
          <Col span={24}>
            <label htmlFor="" className="input-label required-input">
              Website
            </label>
            <Form.Item
              colon={false}
              name="website"
              className="form-control"
              rules={[
                { required: true, message: 'Website url is required' },
                { validator: validateUrl },
              ]}
            >
              <Input
                placeholder="Enter Website URL"
                name="websiteUrl"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label htmlFor="" className="input-label required-input">
              Email
            </label>
            <Form.Item
              colon={false}
              name="email"
              className="form-control"
              rules={[
                { required: true, message: 'Email is required' },
                { type: 'email', message: 'Enter a valid email' },
              ]}
            >
              <Input
                placeholder="example@gmail.com"
                name="email"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <label className="input-label required-input">Phone number</label>
            <Form.Item
              name="phone"
              className="phone-number-form"
              rules={[
                { required: true, message: 'Phone is required' },
                { validator: validatePhone },
              ]}
            >
              <PhoneInput
                country={phone?.isoCode.toLowerCase() || 'us'}
                onChange={(value: string, data: any) => {
                  setPhone({
                    dialCode: '+' + data?.dialCode,
                    number: '+' + value,
                    isoCode: data.countryCode.toUpperCase(),
                  })
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {orgOrganizers?.length > 0 && (
          <EventManager
            selectedOrganizer={selectedOrganizer}
            setSelectedOrganizer={setSelectedOrganizer}
            orgOrganizers={orgOrganizers}
            defaultOrganizer={defaultOrganizer}
            componentname="League"
          />
        )}

        <div className="mt-15px event-button-cont">
          <h5
            className="flex gap-10px align-center pointer"
            onClick={() => handleBack()}
          >
            <CaretLeft size={20} color="#000" />
            <span className="text-15 w-500">Back</span>
          </h5>
          <Button
            className="stepper-button"
            disabled={leagueStep2Loading}
            htmlType="submit"
          >
            {leagueStep2Loading ? (
              <ButtonSpinner size={20} color="#000" />
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default Step2
